import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import RedirectToFirstAccessibleRoute from './components/auth/RedirectToFirstAccessibleRoute';
import NotFoundPage from './components/pages/NotFound';
import { AuthProvider } from './hooks/useAuth';
import { PROTECTED_ROUTES, PUBLIC_ROUTES } from './config/routes';
import { queryClient } from './queryClient';
import { formTokensCss } from '@kioskgaming/ui';

const LegacyAgentShopRedirect: React.FC = () => {
  const { shopId } = useParams<{ shopId: string }>();
  return <Navigate to={shopId ? `/shops/${encodeURIComponent(shopId)}` : '/shops'} replace />;
};

export const AppRoutes: React.FC = () => (
  <Routes>
    {PUBLIC_ROUTES.map(route => {
      const Element = route.element;
      return <Route key={route.path} path={route.path} element={<Element />} />;
    })}

    {/* Backward-compatible redirect for legacy shop detail URLs */}
    <Route
      path="/agents/:agentId/shops/:shopId"
      element={
        <ProtectedRoute requiredPermission="agentManagement">
          <LegacyAgentShopRedirect />
        </ProtectedRoute>
      }
    />

    <Route
      path="/"
      element={
        <ProtectedRoute>
          <RedirectToFirstAccessibleRoute />
        </ProtectedRoute>
      }
    />

    {PROTECTED_ROUTES.map(route => {
      const Element = route.element;
      const content = route.layout === 'main'
        ? (
          <Layout>
            <Element />
          </Layout>
        )
        : <Element />;

      return (
        <Route
          key={route.path}
          path={route.path}
          element={(
            <ProtectedRoute
              requiredPermission={route.permission}
              requiredRole={route.superAdminOnly ? ['super_admin', 'system_admin'] : undefined}
            >
              {content}
            </ProtectedRoute>
          )}
        />
      );
    })}

    <Route
      path="*"
      element={
        <ProtectedRoute>
          <Layout>
            <NotFoundPage />
          </Layout>
        </ProtectedRoute>
      }
    />
  </Routes>
);

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <style>{formTokensCss}</style>
      <AuthProvider>
        <Router>
          <div className="App">
            <AppRoutes />

            {/* Toast notifications */}
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: 'hsl(var(--card))',
                  color: 'hsl(var(--card-foreground))',
                  border: '1px solid hsl(var(--border))',
                },
              }}
            />
          </div>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
